// src/components/exam-builder/SectionItemsModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Trash2,
  ArrowUp,
  ArrowDown,
  PlusCircle,
  Pencil,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ItemPickerModal from "./ItemPickerModal";
import { updateExam } from "../../services/api/exams";
import CustomEditor from "../common/Editor";
import { htmlToText } from "../../utils/htmlToText";

// ✅ اضافه‌شده‌ها برای ادیت
import { httpRequest } from "../../utils/httpRequest";
import { toast } from "react-toastify";
import ItemQuickCreateModal from "./ItemQuickCreateModal";

export default function SectionItemsModal({
  isOpen,
  onClose,
  token,
  examId,
  sectionIndex,
  sections,
  setSections,
  ensureDraft,
  toApiSections,
  categoryId,
}) {
  const PART_PALETTE = [
    { badge: "bg-blue-50 text-blue-700", left: "border-l-4 border-blue-400" },
    {
      badge: "bg-green-50 text-green-700",
      left: "border-l-4 border-green-400",
    },
    {
      badge: "bg-purple-50 text-purple-700",
      left: "border-l-4 border-purple-400",
    },
    {
      badge: "bg-amber-50 text-amber-700",
      left: "border-l-4 border-amber-400",
    },
    { badge: "bg-pink-50 text-pink-700", left: "border-l-4 border-pink-400" },
    { badge: "bg-teal-50 text-teal-700", left: "border-l-4 border-teal-400" },
  ];

  const { t } = useTranslation();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedPartId, setSelectedPartId] = useState("");
  const [newPartId, setNewPartId] = useState("");
  const [showOnlySelectedPart, setShowOnlySelectedPart] = useState(false);
  const [bulkPartForm, setBulkPartForm] = useState({
    from: "",
    to: "",
    partId: "",
  });

  // ✅ اضافه: مدال ویرایش آیتم
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [itemEditIndex, setItemEditIndex] = useState(null);

  const section = useMemo(
    () =>
      sections?.[sectionIndex] || {
        items: [],
        meta: { ui: { layout: "single" } },
      },
    [sections, sectionIndex]
  );

  const layout =
    section?.meta?.ui?.layout === "two_pane" ? "two_pane" : "single";

  const parts = Array.isArray(section?.parts) ? section.parts : [];

  const visibleItems =
    showOnlySelectedPart && selectedPartId
      ? section.items.filter((it) => it.anchorId === selectedPartId)
      : section.items;

  const ensureSectionParts = () =>
    Array.isArray(section?.parts) ? [...section.parts] : [];

  const selectFirstIfNone = (nextParts) => {
    if (!selectedPartId && nextParts.length) setSelectedPartId(nextParts[0].id);
  };

  const setSectionLayout = async (nextLayout) => {
    const next = [...sections];
    const sec = { ...(next[sectionIndex] || {}) };
    const prevMeta = sec.meta || {};
    const prevUi = prevMeta.ui || {};
    sec.meta = { ...prevMeta, ui: { ...prevUi, layout: nextLayout } };
    next[sectionIndex] = sec;
    setSections(next);
    await autosave(next);
  };

  const addPart = async () => {
    const pid = (newPartId || "").trim();
    if (!pid) return;
    const next = [...sections];
    const sec = { ...(next[sectionIndex] || {}) };
    const ps = ensureSectionParts();
    if (ps.some((p) => p.id === pid)) {
      setSelectedPartId(pid);
      setNewPartId("");
      return;
    }
    ps.push({
      id: pid,
      title: { fa: pid, en: pid },
      promptHtml: "",
      passageHtml: "",
      blocks: [],
    });
    sec.parts = ps;
    next[sectionIndex] = sec;
    setSections(next);
    setSelectedPartId(pid);
    setNewPartId("");
    await autosave(next);
  };

  const removePart = async (pid) => {
    if (!pid) return;
    const next = [...sections];
    const sec = { ...(next[sectionIndex] || {}) };
    const ps = ensureSectionParts().filter((p) => p.id !== pid);
    const arr = Array.isArray(sec.items)
      ? sec.items.map((it) =>
          it.anchorId === pid ? { ...it, anchorId: undefined } : it
        )
      : [];
    sec.items = arr;
    sec.parts = ps;
    next[sectionIndex] = sec;
    setSections(next);
    setSelectedPartId(ps[0]?.id || "");
    await autosave(next);
  };

  const renamePartId = async (oldId, newId) => {
    const nid = (newId || "").trim();
    if (!oldId || !nid || oldId === nid) return;
    const next = [...sections];
    const sec = { ...(next[sectionIndex] || {}) };

    const ps = ensureSectionParts().map((p) =>
      p.id === oldId ? { ...p, id: nid } : p
    );
    const arr = Array.isArray(sec.items)
      ? sec.items.map((it) =>
          it.anchorId === oldId ? { ...it, anchorId: nid } : it
        )
      : [];

    sec.parts = ps;
    sec.items = arr;
    next[sectionIndex] = sec;
    setSections(next);
    setSelectedPartId(nid);
    await autosave(next);
  };

  const updatePart = async (pid, patch) => {
    const next = [...sections];
    const sec = { ...(next[sectionIndex] || {}) };
    const ps = ensureSectionParts().map((p) =>
      p.id === pid ? { ...p, ...patch } : p
    );
    sec.parts = ps;
    next[sectionIndex] = sec;
    setSections(next);
    await autosave(next);
  };

  const addBlock = async (pid) => {
    const next = [...sections];
    const sec = { ...(next[sectionIndex] || {}) };
    const ps = ensureSectionParts();
    const i = ps.findIndex((p) => p.id === pid);
    if (i === -1) return;
    const blocks = Array.isArray(ps[i].blocks) ? [...ps[i].blocks] : [];
    blocks.push({ id: `B${blocks.length + 1}`, html: "", from: 1, to: 1 });
    ps[i] = { ...ps[i], blocks };
    sec.parts = ps;
    next[sectionIndex] = sec;
    setSections(next);
    await autosave(next);
  };

  const updateBlock = async (pid, bi, patch) => {
    const next = [...sections];
    const sec = { ...(next[sectionIndex] || {}) };
    const ps = ensureSectionParts();
    const i = ps.findIndex((p) => p.id === pid);
    if (i === -1) return;
    const blocks = Array.isArray(ps[i].blocks) ? [...ps[i].blocks] : [];
    blocks[bi] = { ...blocks[bi], ...patch };
    ps[i] = { ...ps[i], blocks };
    sec.parts = ps;
    next[sectionIndex] = sec;
    setSections(next);
    await autosave(next);
  };

  const removeBlock = async (pid, bi) => {
    const next = [...sections];
    const sec = { ...(next[sectionIndex] || {}) };
    const ps = ensureSectionParts();
    const i = ps.findIndex((p) => p.id === pid);
    if (i === -1) return;
    const blocks = (ps[i].blocks || []).filter((_, k) => k !== bi);
    ps[i] = { ...ps[i], blocks };
    sec.parts = ps;
    next[sectionIndex] = sec;
    setSections(next);
    await autosave(next);
  };

  const findPartIndex = (parts = [], pid) =>
    pid ? parts.findIndex((p) => p.id === pid) : -1;

  const partTitle = (parts = [], pid) => pid || "بدون پارت";

  const setItemPart = async (idx, pid) => {
    const next = [...sections];
    const sec = { ...(next[sectionIndex] || {}) };
    const arr = Array.isArray(sec.items) ? [...sec.items] : [];
    arr[idx] = { ...arr[idx], anchorId: pid || undefined };
    sec.items = arr;

    if (pid && !(sec.parts || []).some((p) => p.id === pid)) {
      const ps = Array.isArray(sec.parts) ? [...sec.parts] : [];
      ps.push({
        id: pid,
        title: { fa: pid, en: pid },
        promptHtml: "",
        passageHtml: "",
      });
      sec.parts = ps;
    }

    next[sectionIndex] = sec;
    setSections(next);
    await autosave(next);
  };

  const setBulk = (patch) => setBulkPartForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (parts.length) selectFirstIfNone(parts);
    else setSelectedPartId("");
  }, [sectionIndex, JSON.stringify(parts)]);

  if (!isOpen) return null;

  const applyBulkAssign = async () => {
    const fromN = Number(bulkPartForm.from);
    const toN = Number(bulkPartForm.to);
    const pid = (bulkPartForm.partId || "").trim();
    if (!fromN || !toN || fromN < 1 || toN < 1) return;
    if (!pid) return;
    if (!section?.items?.length) return;

    const max = section.items.length;
    const start = Math.max(0, Math.min(fromN, toN) - 1);
    const end = Math.min(max - 1, Math.max(fromN, toN) - 1);

    const next = [...sections];
    const sec = { ...(next[sectionIndex] || {}) };
    const arr = Array.isArray(sec.items) ? [...sec.items] : [];

    for (let i = start; i <= end; i++) arr[i] = { ...arr[i], anchorId: pid };
    sec.items = arr;

    const exists = (sec.parts || []).some((p) => p.id === pid);
    if (!exists) {
      const nextParts = Array.isArray(sec.parts) ? [...sec.parts] : [];
      nextParts.push({
        id: pid,
        title: { fa: pid, en: pid },
        promptHtml: "",
        passageHtml: "",
      });
      sec.parts = nextParts;
    }

    next[sectionIndex] = sec;
    setSections(next);

    await autosave(next);
    setBulkPartForm({ from: "", to: "", partId: "" });
  };

  const autosave = async (nextSections) => {
    try {
      const id = examId || (await ensureDraft());
      const payload = { sections: toApiSections(nextSections) };
      const res = await updateExam(token, id, payload);
      if (!(res?.status === 200 || res?.data?._id))
        console.error("Autosave failed", res);
    } catch (e) {
      console.error("Autosave error", e);
    }
  };

  const removeAt = async (idx) => {
    const nextSections = (() => {
      const clone = [...sections];
      const sec = { ...(clone[sectionIndex] || { items: [] }) };
      sec.items = (sec.items || []).filter((_, i) => i !== idx);
      clone[sectionIndex] = sec;
      return clone;
    })();
    setSections(nextSections);
    await autosave(nextSections);
  };

  const moveItem = async (idx, dir) => {
    const target = idx + dir;
    const arrLen = section.items?.length || 0;
    if (target < 0 || target >= arrLen) return;

    const nextSections = (() => {
      const clone = [...sections];
      const sec = { ...(clone[sectionIndex] || { items: [] }) };
      const arr = [...(sec.items || [])];

      const sourcePart = arr[idx]?.anchorId;
      const targetPart = arr[target]?.anchorId;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      arr[target] = { ...arr[target], anchorId: targetPart };
      arr[idx] = { ...arr[idx], anchorId: sourcePart };

      sec.items = arr;
      clone[sectionIndex] = sec;
      return clone;
    })();

    setSections(nextSections);
    await autosave(nextSections);
  };

  const changePoints = async (idx, newPoints) => {
    const points = Number(newPoints);
    if (Number.isNaN(points) || points < 0) return;
    const nextSections = (() => {
      const clone = [...sections];
      const sec = { ...(clone[sectionIndex] || { items: [] }) };
      const arr = [...(sec.items || [])];
      arr[idx] = { ...arr[idx], points };
      sec.items = arr;
      clone[sectionIndex] = sec;
      return clone;
    })();
    setSections(nextSections);
    await autosave(nextSections);
  };

  const toggleRequired = async (idx) => {
    const nextSections = (() => {
      const clone = [...sections];
      const sec = { ...(clone[sectionIndex] || { items: [] }) };
      const arr = [...(sec.items || [])];
      const cur = arr[idx]?.required !== false;
      arr[idx] = { ...arr[idx], required: !cur };
      sec.items = arr;
      clone[sectionIndex] = sec;
      return clone;
    })();
    setSections(nextSections);
    await autosave(nextSections);
  };

  const addPickedItems = async (picked) => {
    const nextSections = (() => {
      const clone = [...sections];
      const sec = { ...(clone[sectionIndex] || { items: [] }) };
      const curr = sec.items || [];
      const additions = (picked || []).map((it) => ({
        item: it._id,
        stem: it.stem,
        points: typeof it.points === "number" ? it.points : 1,
        required: true,
      }));
      sec.items = [...curr, ...additions];
      clone[sectionIndex] = sec;
      return clone;
    })();
    setSections(nextSections);
    await autosave(nextSections);
  };

  // ✅ ادیت آیتم داخل سکشن
  const handleEditItem = async (idx) => {
    try {
      const secItem = section.items?.[idx];
      if (!secItem) return;

      const rawItem = secItem.item;
      const itemId =
        rawItem && typeof rawItem === "object" ? rawItem._id : rawItem;

      if (!itemId) {
        toast.error("شناسه سؤال پیدا نشد");
        return;
      }

      const res = await httpRequest(
        "GET",
        `/item/${itemId}`,
        {},
        { "x-access-token": token }
      );
      const doc = res?.data?.data || res?.data;

      if (!doc?._id) {
        toast.error("سؤال پیدا نشد");
        return;
      }

      setItemToEdit(doc);
      setItemEditIndex(idx);
      setItemModalOpen(true);
    } catch (err) {
      toast.error(err?.message || "خطا در دریافت سؤال");
    }
  };

  const handleItemUpdated = async (doc) => {
    if (!doc?._id || itemEditIndex == null) {
      setItemModalOpen(false);
      setItemToEdit(null);
      setItemEditIndex(null);
      return;
    }

    const nextSections = (() => {
      const clone = [...sections];
      const sec = { ...(clone[sectionIndex] || { items: [] }) };
      const arr = [...(sec.items || [])];

      const old = arr[itemEditIndex];
      if (!old) return sections;

      arr[itemEditIndex] = {
        ...old,
        item: doc, // می‌تونی فقط doc._id هم نگه داری؛ اینجا doc کامل مفیده
        stem: doc.stem,
        points:
          typeof old.points === "number"
            ? old.points
            : typeof doc.points === "number"
            ? doc.points
            : 1,
      };

      sec.items = arr;
      clone[sectionIndex] = sec;
      return clone;
    })();

    setSections(nextSections);
    await autosave(nextSections);

    setItemModalOpen(false);
    setItemToEdit(null);
    setItemEditIndex(null);
  };

  // ✅ کلیک روی بک‌دراپ: اگر مدال داخلی بازه، مدال اصلی بسته نشه
  const handleBackdropClick = () => {
    if (isPickerOpen || itemModalOpen) return;
    onClose?.();
  };

  return (
    <div
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-5xl rounded-2xl shadow-xl p-4 md:p-6 max-h-[80vh] overflow-y-scroll"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold">
            {t("section_items.title", { index: (sectionIndex ?? 0) + 1 })}
          </h2>
          <button
            className="p-2 rounded-lg hover:bg-gray-100"
            onClick={onClose}
            aria-label="close"
          >
            <X />
          </button>
        </div>

        {/* ✅ کنترل Layout سکشن */}
        <div className="mb-4 p-3 border rounded-xl bg-gray-50 flex flex-wrap items-center gap-3">
          <label className="text-sm font-medium">Layout:</label>
          <select
            className="border rounded px-2 py-1"
            value={layout}
            onChange={(e) => setSectionLayout(e.target.value)}
          >
            <option value="single">Single (استاندارد)</option>
            <option value="two_pane">Two-pane (Reading)</option>
          </select>
          <span className="text-xs text-gray-500">
            {layout === "two_pane"
              ? "در این حالت، Passage هر Part در ستون چپ نمایش داده می‌شود."
              : "نمای استاندارد بدون ستون Passage."}
          </span>
        </div>

        {parts.length === 0 ? (
          <div className="mb-3 p-3 border rounded-xl bg-gray-50">
            <button
              className="px-3 py-2 rounded bg-gray-900 text-white text-sm"
              onClick={() => {
                const next = [...sections];
                const sec = { ...(next[sectionIndex] || {}) };
                sec.parts = [
                  {
                    id: "P1",
                    title: { fa: "P1", en: "P1" },
                    promptHtml: "",
                    passageHtml: "", // ✅
                    blocks: [],
                  },
                ];
                next[sectionIndex] = sec;
                setSections(next);
              }}
            >
              فعال‌کردن پارت‌بندی
            </button>
          </div>
        ) : (
          <>
            <div className="mb-3 p-3 border rounded-xl bg-gray-50">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-base font-semibold">پارت‌ها:</span>

                {parts.map((p, i) => {
                  const count = (section.items || []).filter(
                    (it) => it.anchorId === p.id
                  ).length;
                  const isActive = selectedPartId === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPartId(p.id)}
                      className={`px-2 py-1 text-xs rounded-full border ${
                        isActive
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-gray-800"
                      }`}
                      title={p.id}
                    >
                      {p.id} • {count}
                    </button>
                  );
                })}

                <span className="px-2 py-1 text-xs rounded-full border bg-white text-gray-600">
                  بدون پارت •{" "}
                  {(section.items || []).filter((it) => !it.anchorId).length}
                </span>

                <div className="ms-auto flex items-center gap-2">
                  {/* ورودی ساخت پارت جدید (اختیاری) */}
                  <input
                    className="border rounded px-2 py-1 text-xs"
                    placeholder="شناسه پارت جدید"
                    value={newPartId}
                    onChange={(e) => setNewPartId(e.target.value)}
                  />
                  <button
                    onClick={addPart}
                    className="px-2 py-1 text-xs rounded bg-gray-800 text-white"
                  >
                    افزودن پارت
                  </button>

                  {selectedPartId && (
                    <button
                      onClick={() => removePart(selectedPartId)}
                      className="px-2 py-1 text-xs rounded bg-red-600 text-white"
                    >
                      حذف پارت انتخاب‌شده
                    </button>
                  )}
                </div>
              </div>

              {/* ادیتور پارت انتخاب‌شده */}
              {selectedPartId && (
                <div className="mt-3 border-t pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold">
                      ویرایش پارت: {selectedPartId}
                    </span>
                  </div>

                  {/* promptHtml */}
                  <label className="block text-xs text-gray-600 mb-1">
                    صورت‌سؤال مشترک (HTML)
                  </label>
                  {typeof CustomEditor === "function" ? (
                    <CustomEditor
                      value={
                        parts.find((p) => p.id === selectedPartId)
                          ?.promptHtml || ""
                      }
                      onEditorChange={(content) =>
                        updatePart(selectedPartId, { promptHtml: content })
                      }
                      height={160}
                    />
                  ) : (
                    <textarea
                      className="w-full border rounded px-2 py-1 text-sm"
                      rows={6}
                      value={
                        parts.find((p) => p.id === selectedPartId)
                          ?.promptHtml || ""
                      }
                      onChange={(e) =>
                        updatePart(selectedPartId, {
                          promptHtml: e.target.value,
                        })
                      }
                    />
                  )}

                  {/* ✅ passageHtml فقط اگر two_pane */}
                  {layout === "two_pane" && (
                    <>
                      <label className="block text-xs text-gray-600 mb-1 mt-3">
                        متن/Passage (HTML) – ستون چپ در حالت Reading
                      </label>
                      {typeof CustomEditor === "function" ? (
                        <CustomEditor
                          value={
                            parts.find((p) => p.id === selectedPartId)
                              ?.passageHtml || ""
                          }
                          onEditorChange={(content) =>
                            updatePart(selectedPartId, { passageHtml: content })
                          }
                          height={200}
                        />
                      ) : (
                        <textarea
                          className="w-full border rounded px-2 py-1 text-sm"
                          rows={8}
                          value={
                            parts.find((p) => p.id === selectedPartId)
                              ?.passageHtml || ""
                          }
                          onChange={(e) =>
                            updatePart(selectedPartId, {
                              passageHtml: e.target.value,
                            })
                          }
                        />
                      )}
                    </>
                  )}

                  {/* Blocks */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        بلوک‌های زیر-صورت‌سؤال
                      </span>
                      <button
                        className="px-2 py-1 text-xs rounded bg-gray-800 text-white"
                        onClick={() => addBlock(selectedPartId)}
                      >
                        افزودن بلوک
                      </button>
                    </div>

                    {(
                      parts.find((p) => p.id === selectedPartId)?.blocks || []
                    ).map((b, bi) => (
                      <div key={b.id || bi} className="border rounded p-2 mb-2">
                        <div className="flex gap-2 mb-2">
                          <input
                            type="number"
                            className="w-24 border rounded px-2 py-1 text-sm"
                            value={b.from ?? ""}
                            min={1}
                            placeholder="از"
                            onChange={(e) =>
                              updateBlock(selectedPartId, bi, {
                                from: Number(e.target.value) || undefined,
                              })
                            }
                          />
                          <input
                            type="number"
                            className="w-24 border rounded px-2 py-1 text-sm"
                            value={b.to ?? ""}
                            min={1}
                            placeholder="تا"
                            onChange={(e) =>
                              updateBlock(selectedPartId, bi, {
                                to: Number(e.target.value) || undefined,
                              })
                            }
                          />
                          <button
                            className="ml-auto text-red-600 text-xs"
                            onClick={() => removeBlock(selectedPartId, bi)}
                          >
                            حذف
                          </button>
                        </div>

                        {typeof CustomEditor === "function" ? (
                          <CustomEditor
                            value={b.html || ""}
                            onEditorChange={(content) =>
                              updateBlock(selectedPartId, bi, { html: content })
                            }
                            height={120}
                          />
                        ) : (
                          <textarea
                            className="w-full border rounded px-2 py-1 text-sm"
                            rows={4}
                            value={b.html || ""}
                            onChange={(e) =>
                              updateBlock(selectedPartId, bi, {
                                html: e.target.value,
                              })
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {/* (همون بخشی که در کد قبلی گذاشتید) */}

        {/* لیست آیتم‌های سکشن */}
        <div className="border rounded-xl overflow-hidden">
          {section.items?.length ? (
            <ul className="divide-y">
              {section.items.map((it, idx) => {
                const pid = it.anchorId || "";
                const pIdx = findPartIndex(parts, pid);
                const leftClass =
                  pIdx >= 0
                    ? PART_PALETTE[pIdx % PART_PALETTE.length].left
                    : "";
                const badgeClass =
                  pIdx >= 0
                    ? PART_PALETTE[pIdx % PART_PALETTE.length].badge
                    : "bg-gray-100 text-gray-700";

                const safeStem = htmlToText(
                  it.stem
                    ? String(it.stem.fa || it.stem.en || "")
                    : String(it.item?.stem?.fa || it.item?.stem?.en || "")
                );

                return (
                  <li
                    key={idx}
                    className={`p-3 flex items-center gap-3 ${leftClass}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {t("section_items.item_row_title", { n: idx + 1 })}
                        </div>
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded-full border border-gray-200 ${badgeClass}`}
                        >
                          {partTitle(parts, pid)}
                        </span>
                      </div>

                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {safeStem}
                      </p>

                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
                        <label className="inline-flex items-center gap-1">
                          {t("section_items.points") || "امتیاز"}:
                          <input
                            type="number"
                            min={0}
                            className="w-20 border rounded px-2 py-1"
                            value={it.points ?? 1}
                            onChange={(e) => changePoints(idx, e.target.value)}
                          />
                        </label>

                        <label className="inline-flex items-center gap-1">
                          {t("section_items.required") || "الزامی"}:
                          <input
                            type="checkbox"
                            checked={it.required !== false}
                            onChange={() => toggleRequired(idx)}
                          />
                        </label>

                        {parts.length > 0 && (
                          <label className="inline-flex items-center gap-1">
                            پارت:
                            <select
                              className="border rounded px-2 py-1"
                              value={pid}
                              onChange={(e) => setItemPart(idx, e.target.value)}
                            >
                              <option value="">بدون پارت</option>
                              {parts.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.id}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveItem(idx, -1)}
                        className="p-2 rounded-lg hover:bg-gray-100"
                        title={t("section_items.move_up")}
                      >
                        <ArrowUp size={18} />
                      </button>

                      <button
                        onClick={() => moveItem(idx, +1)}
                        className="p-2 rounded-lg hover:bg-gray-100"
                        title={t("section_items.move_down")}
                      >
                        <ArrowDown size={18} />
                      </button>

                      {/* ✅ دکمه ادیت */}
                      <button
                        onClick={() => handleEditItem(idx)}
                        className="p-2 rounded-lg hover:bg-gray-100"
                        title={t("section_items.edit_item", "ویرایش سؤال")}
                      >
                        <Pencil size={18} />
                      </button>

                      {/* ✅ حذف */}
                      <button
                        onClick={() => removeAt(idx)}
                        className="p-2 rounded-lg text-red-600 hover:bg-red-100"
                        title={t("section_items.remove")}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="p-4 text-sm text-gray-500">
              {t("section_items.empty")}
            </div>
          )}
        </div>

        {/* ... بقیه فرم bulk assign و اکشن‌ها مثل قبل ... */}
        <div className="mb-3 p-3 border rounded-xl bg-gray-50 flex flex-col md:flex-row flex-wrap md:items-start justify-center gap-3 mt-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              {t("section_items.from_question") || "از سؤال"}
            </label>
            <input
              type="number"
              min={1}
              className="w-28 border rounded px-2 py-1"
              value={bulkPartForm.from}
              onChange={(e) => setBulk({ from: e.target.value })}
              placeholder="1"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              {t("section_items.to_question") || "تا سؤال"}
            </label>
            <input
              type="number"
              min={1}
              className="w-28 border rounded px-2 py-1"
              value={bulkPartForm.to}
              onChange={(e) => setBulk({ to: e.target.value })}
              placeholder="10"
            />
          </div>

          <div className="flex-1">
            <label className="block text-xs text-gray-600 mb-1">
              {t("section_items.part_id") || "شناسه پارت"}
            </label>

            <div className="flex gap-2">
              <select
                className="border rounded px-2 py-1"
                value=""
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) setBulk({ partId: val });
                }}
              >
                <option value="">
                  {t("section_items.pick_from_parts") || "انتخاب از پارت‌ها"}
                </option>
                {parts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.id}
                  </option>
                ))}
              </select>

              <input
                className="flex-1 border rounded px-2 py-1"
                placeholder="مثلاً L1 یا R2…"
                value={bulkPartForm.partId}
                onChange={(e) => setBulk({ partId: e.target.value })}
              />
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              {t("section_items.part_hint") ||
                "اگر پارت در لیست نبود، شناسه را دستی بنویس (مثل L1, L2, …)."}
            </p>
          </div>

          <div>
            <button
              onClick={applyBulkAssign}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              {t("section_items.apply_range") || "اعمال روی بازه"}
            </button>
          </div>
        </div>

        <div className="mt-4 flex justify-between">
          <button
            onClick={() => setIsPickerOpen(true)}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2"
          >
            <PlusCircle className="w-4 h-4" />
            {t("section_items.add_items")}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50"
          >
            {t("common.close")}
          </button>
        </div>
      </div>

      {/* Picker داخلی */}
      <ItemPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        token={token}
        onPick={addPickedItems}
        categoryId={categoryId}
        examId={examId}
      />

      {/* ✅ مدال ادیت آیتم */}
      <ItemQuickCreateModal
        isOpen={itemModalOpen}
        onClose={() => {
          setItemModalOpen(false);
          setItemToEdit(null);
          setItemEditIndex(null);
        }}
        token={token}
        mode="edit"
        item={itemToEdit}
        onCreated={handleItemUpdated}
      />
    </div>
  );
}
